import React, { FunctionComponent, useEffect, useState } from 'react';

import FullPageWrapper from 'scenes/common/components/FullPageWrapper/FullPageWrapper';
import {
  isLoadingWithNoData,
  isApiError,
  isApiReferral,
} from 'scenes/common/helpers/apiData';

import analytics from 'scenes/retrieve/analytics';
import {
  RetrieveFunnelState,
  RetrieveStepProps,
} from 'scenes/retrieve/flows/types';
import withRetrieveQuote from 'scenes/retrieve/components/HOC/withRetrieveQuote';
import RetrieveForm from 'scenes/common/components/Forms/RetrieveForm';
import { ApiState } from 'scenes/common/types';
import {
  colesContactCenter,
  insuranceBaseUrl,
} from 'scenes/common/helpers/contacts';
import { TextSmall } from 'components/chroma/extended';
import { Text } from 'components/chroma';

const errorMessage = (): JSX.Element => (
  <TextSmall>
    Unable to retrieve your quote based on the details provided. Please try
    again or return to the{' '}
    <a href={insuranceBaseUrl()} target="_blank" rel="noopener noreferrer">
      Coles Insurance home page
    </a>{' '}
    to do another quote.
  </TextSmall>
);

const apiReferralMessage = (): JSX.Element => (
  <>
    <Text>
      <strong>Something&apos;s gone wrong.</strong>
    </Text>
    <Text>
      Please call us on <strong>1300 265 374</strong> as we need to clarify your
      policy details in order to process your quote.
    </Text>
    <Text>
      Our contact hours can be found{' '}
      <a href={colesContactCenter()} target="_blank" rel="noopener noreferrer">
        here
      </a>
      .
    </Text>
  </>
);

const getQuoteDetails = (funnelState: RetrieveFunnelState) => {
  if (
    funnelState.apiResponses.motorQuoteDetails.state !== ApiState.NeverLoaded
  ) {
    return funnelState.apiResponses.motorQuoteDetails;
  }
  if (
    funnelState.apiResponses.homeQuoteDetails.state !== ApiState.NeverLoaded
  ) {
    return funnelState.apiResponses.homeQuoteDetails;
  }

  if (
    funnelState.apiResponses.landlordQuoteDetails.state !== ApiState.NeverLoaded
  ) {
    return funnelState.apiResponses.landlordQuoteDetails;
  }

  return funnelState.apiResponses.motorQuoteDetails;
};

const FormPage: FunctionComponent<RetrieveStepProps> = ({
  funnelState,
  onRetrieveQuote,
  defaultQuoteNumber,
}) => {
  useEffect(() => {
    document.title = 'Retrieve quote - Coles Insurance';
  }, []);

  const [errorMessageElement, setErrorMessage] = useState<
    JSX.Element | undefined
  >(undefined);

  const quoteDetails = getQuoteDetails(funnelState);

  useEffect(() => {
    if (isApiError(quoteDetails)) {
      setErrorMessage(errorMessage);
    } else if (isApiReferral(quoteDetails)) {
      setErrorMessage(apiReferralMessage);
    }
    return () => {
      setErrorMessage(undefined);
    };
  }, [quoteDetails]);

  return (
    <FullPageWrapper
      onClickCallUs={() => analytics().callUs(funnelState)}
      isLoading={
        isLoadingWithNoData(funnelState.apiResponses.motorQuoteDetails) ||
        isLoadingWithNoData(funnelState.apiResponses.homeQuoteDetails)
      }
    >
      <RetrieveForm
        errorMessageElement={errorMessageElement}
        onSubmitCallback={onRetrieveQuote}
        defaultQuoteNumber={defaultQuoteNumber}
      />
    </FullPageWrapper>
  );
};

export default withRetrieveQuote(FormPage);
